import { Observable, Subject, forkJoin, of } from 'rxjs';
import { ModeFacturationModel, GroupeFactureModel, Article, LigneFacture, Acompte, FacturationSearchData, MissionSearchData, Entite, Echeancier, ConventionSearchData, DataSave, ObjetFact } from '../models/facturation/model.facturation';
import { map, mergeMap } from 'rxjs/operators';
import { QueovalItemModel, FileModel, QueovalDate } from '../models/global/class.global';
import { transformQueovalDateToDate, transformDateToQueovalDate, transformQueovalDateToDateOrNull, AddEmptyLine, VerifyAndAffectIfValue } from '../utilities/utilities.function';
import { HttpService } from '../services/dataAccess.service';
import { CoreService } from '../services/core.service';
import { ReconduiteModel } from '../models/facturation/reconduite.model';
import { ReglementModel, FichierSEPA } from '../models/facturation/reglement.model';
import { Select } from '../models/utilities/utilities.model';
import { Injectable } from '@angular/core';


/**
 * ModeFacturationService
 *
 * Service global du mode de facturation.
 *
 * Tous les appels api ainsi que les modifications du models passent par ce service.
 */
@Injectable({
  providedIn: 'root'
})

export class ModeFacturationService {

  /**
   * Model global
   */
  public modeFacturationModel: ModeFacturationModel;

  /**
   * Model reglement
   */
  public reglementModel: ReglementModel;

  /**
   * Model recherche facture
   */
  public searchFactureModel: FacturationSearchData;

  /**
   * Model recherche mission
   */
  public searchMissionModel: MissionSearchData;

  /**
  * Model recherche convention
  */
  public searchConventionModel: ConventionSearchData;

  /**
   * Model pour les reconduites de mission
   */
  public reconduiteModel: ReconduiteModel;


  // Subject permettant de récupérer le model global de façon asynchrone
  public modeFacturationModelAsync: Subject<ModeFacturationModel> = new Subject<ModeFacturationModel>();

  // Subject permettant la navigation
  public navigationModeFacturation: Subject<{ niveau: number, mode: number, idClient: string }> = new Subject<{ niveau: number, mode: number, idClient: string }>();
  /**
   * @param {HttpService} http
   */
  constructor(
    protected http: HttpService,
    private indParam: CoreService,
  ) {
    this.modeFacturationModel = new ModeFacturationModel();
    this.reglementModel = new ReglementModel();
  }

  // #region getter and setter synchrone

  /**
  * Getter synchrone du model global
  */
  get FacturationModel(): ModeFacturationModel {
    return this.modeFacturationModel;
  }

  /**
   * Setter synchrone du model global
   */
  set FacturationModel(modeFacturationModel: ModeFacturationModel) {
    this.modeFacturationModel = modeFacturationModel;
  }

  // #endregion

  // #region getter and setter asynchrone

  /**
   * Getter asynchrone du model global
   */
  getFacturationModelAsync(): Observable<ModeFacturationModel> {
    return this.modeFacturationModelAsync.asObservable();
  }

  /**
   * Setter asynchrone du model global
   */
  setFacturationModelAsync(modeFacturationModel: ModeFacturationModel): void {
    this.modeFacturationModelAsync.next(modeFacturationModel);
  }

  /**
   * point d'entrée du composant ariane
   */
  getNavigationAsync(): Observable<{ niveau: number, mode: number, idClient: string }> {
    return this.navigationModeFacturation.asObservable();
  }

  /**
   * Méthode permettant la navigation
   * @param {any} value parametres de la route
   */
  setNavigationAsync(value: { niveau: number, mode: number, idClient: string }): void {
    this.navigationModeFacturation.next(value);
  }

  // #endregion

  // #region listes sans parametres chargement à l'initialisation de modeFacturationComponent

  /**
   * Forkjoin appelant l'ensemble des listes qui n'ont pas besoins de parametres
   */
  getListes(idFacture?: string): Observable<boolean> {
    return forkJoin([this.getOrdreLigne(), this.getUnite(), this.getTxTva(), this.getGroupeLigne(idFacture), this.getEtatSepa(), this.getReglement(), this.getPeriodicite(), this.getGroupeFamille()]).pipe(
      map((res: Array<boolean>) => {
        return true;
      })
    );
  }

  /**
   * retourne et affecte la liste des unites
   */
  getUnite(): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/UNITE/0`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.unite = res.formulaire.unite;
        AddEmptyLine(this.modeFacturationModel.formulaire.unite);
        return true;
      })
    );
  }

  /**
   * retourne et affecte la liste des ordres de lignes
   */
  getOrdreLigne(): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/ORDRELIGNE/0`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.ordreLigne = res.formulaire.ordreLigne;
        AddEmptyLine(this.modeFacturationModel.formulaire.ordreLigne);
        return true;
      })
    );
  }

  /**
   * retourne et affecte la liste des tx de tva
   */
  getTxTva(): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/TAUXTAXE/0`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.txTva = res.formulaire.txTva;
        AddEmptyLine(this.modeFacturationModel.formulaire.txTva, false);
        return true;
      })
    );
  }

  /**
   * retourne et affecte la liste des groupes de lignes
   * @param {string} idFacture id de la facture
   */
  getGroupeLigne(idFacture?: string): Observable<boolean> {
    if (!idFacture) {
      idFacture = '0';
    }
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/GROUPELIGNE/${idFacture}`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.groupeLigne = res.formulaire.groupeLigne;
        //transformToSelect(res.formulaire, this.modeFacturationModel.formulaire, 'groupeLigne');
        AddEmptyLine(this.modeFacturationModel.formulaire.groupeLigne);
        this.modeFacturationModel.formulaire.groupeLigne.splice(0, 1);
        return true;
      })
    );
  }

  /**
   * retourne et affecte la liste des etats sepa
   */
  getEtatSepa(): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/ETATSEPA/0`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.etatSepa = res.formulaire.etatSepa;
        //transformToSelect(res.formulaire, this.modeFacturationModel.formulaire, 'etatSepa');
        AddEmptyLine(this.modeFacturationModel.formulaire.etatSepa);
        return true;
      })
    );
  }

  /**
   * retourne et affecte la liste des modes de réglement
   */
  getReglement(): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/MODEREGLEMENT/0`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.modeReglement = res.formulaire.modeReglement;
        //transformToSelect(res.formulaire, this.modeFacturationModel.formulaire, 'modeReglement');
        AddEmptyLine(this.modeFacturationModel.formulaire.modeReglement);
        return true;
      })
    );
  }

  /**
   * retourne et affecte la liste des périodicités
   */
  getPeriodicite(): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/TYPEPERIODICITE/0`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.typePeriodicite = res.formulaire.typePeriodicite;
        //transformToSelect(res.formulaire, this.modeFacturationModel.formulaire, 'typePeriodicite');
        AddEmptyLine(this.modeFacturationModel.formulaire.typePeriodicite);
        this.modeFacturationModel.formulaire.typePeriodicite[0] = { id: 'P', value: 'Aucune periodicité' };
        return true;
      })
    );
  }

  /**
   * retourne et affecte la liste des familles
   */
  getGroupeFamille(idFamilleUnique?: string): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/TYPEGROUPEFAMILLE/0`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.groupeFamille = res.formulaire.groupeFamille;
        //transformToSelect(res.formulaire, this.modeFacturationModel.formulaire, 'groupeFamille');        
        if (idFamilleUnique) {
          const selectUnique: Select = new Select(idFamilleUnique, `Facturation isolée`);
          this.modeFacturationModel.formulaire.groupeFamille.unshift(selectUnique);
        }
        AddEmptyLine(this.modeFacturationModel.formulaire.groupeFamille);

        // if (this.modeFacturationModel.formulaire.groupeFamille.length === 2) {
        //   this.modeFacturationModel.formulaire.groupeFamille.splice(0, 1);
        //   this.modeFacturationModel.dataSave.modeFacturation.groupeFamille = this.modeFacturationModel.formulaire.groupeFamille[0];
        // }
        return true;
      })
    );
  }

  // #endregion

  // #region listes avec paramteres

  /**
   * retourne et affecte la liste des articles
   * @param {ModeFacturationModel} model model
   */
  getArticles(pModel: ModeFacturationModel): Observable<boolean> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'LISTEARTICLE';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        if (!res.formulaire.article || res.formulaire.article.length === 0) {
          this.modeFacturationModel.formulaire.article = new Array<Article>({ id: '0', value: '--------', title: '', tva: '', debours: false, excluBpf: false, isBareme: false });
        } else {
          this.modeFacturationModel.formulaire.article = res.formulaire.article;
        }
        return true;
      })
    );
  }

  /**
  * retourne et affecte la liste des articles
  * @param {ModeFacturationModel} model model
  */
  getTypePrestation(): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/TYPEPRESTATION/0`).pipe(
      map((res: ModeFacturationModel) => {
        if (!res.formulaire.typePrestation || res.formulaire.typePrestation.length === 0) {
          this.modeFacturationModel.formulaire.typePrestation = new Array<Select>({ id: '0', value: '--------' });
        } else {
          this.modeFacturationModel.formulaire.typePrestation = res.formulaire.typePrestation;
          this.modeFacturationModel.formulaire.typePrestation.unshift({ id: '0', value: '--------' });
        }
        return true;
      })
    );
  }

  /**
  * retourne et affecte la liste des articles
  * @param {ModeFacturationModel} model model
  */
  getTypeArticle(): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/TYPEARTICLE/0`).pipe(
      map((res: ModeFacturationModel) => {
        if (!res.formulaire.article || res.formulaire.article.length === 0) {
          this.modeFacturationModel.formulaire.article = new Array<Article>({ id: '0', value: '--------', title: '', tva: '', debours: false, excluBpf: false, isBareme: false });
        } else {
          this.modeFacturationModel.formulaire.article = res.formulaire.article;
          this.modeFacturationModel.formulaire.article.unshift({ id: '0', value: '--------', title: '', tva: '', debours: false, excluBpf: false, isBareme: false });
        }
        return true;
      })
    );
  }

  /**
   * retourne et affecte la liste des acomptes
   * @param {ModeFacturationModel} model model
   */
  getListeAcompte(pModel: ModeFacturationModel): Observable<boolean> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'ACCOMPTE';
    this.modeFacturationModel.formulaire.acompte = new Array<Acompte>();
    this.modeFacturationModel.dataSave.acompte = new Array<Acompte>();
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        if (res.formulaire.acompte && res.formulaire.acompte.length > 0) {
          this.modeFacturationModel.formulaire.acompte = JSON.parse(JSON.stringify(res.formulaire.acompte));
          this.modeFacturationModel.dataSave.acompte = JSON.parse(JSON.stringify(res.formulaire.acompte));
        }
        return true;
      }
      ));
  }

  /**
   * retourne et affecte la liste des millesimes
   * @param {string} idMission id de la mission
   */
  getMillesimes(idMission: string): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/MILLESIME/${idMission}`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.millesimeMission = res.formulaire.millesimeMission;
        //transformToSelect(res.formulaire, this.modeFacturationModel.formulaire, 'millesimeMission');
        AddEmptyLine(this.modeFacturationModel.formulaire.millesimeMission);
        if (this.modeFacturationModel.formulaire.millesimeMission.length === 2) {
          this.modeFacturationModel.formulaire.millesimeMission.splice(0, 1);
          this.modeFacturationModel.dataSave.millesimeMission = this.modeFacturationModel.formulaire.millesimeMission[0];
        }
        return true;
      })
    );
  }

  /**
   * retourne et effecte la liste des entites
   * @param {string} idEntité id de l'entité
   */
  getGroupeFacturation(idEntité: string): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/GROUPEFACTURATION/${idEntité}`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.groupeFacturation = res.formulaire.groupeFacturation;
        //transformToSelect(res.formulaire, this.modeFacturationModel.formulaire, 'groupeFacturation');
        AddEmptyLine(this.modeFacturationModel.formulaire.groupeFacturation);
        if (this.modeFacturationModel.formulaire.groupeFacturation.length === 2) {
          this.modeFacturationModel.dataSave.modeFacturation.groupeFacturation = this.modeFacturationModel.formulaire.groupeFacturation[1];
          this.getArticles(this.modeFacturationModel).subscribe();
        }
        return true;
      })
    );
  }

  // #endregion

  // #region info du financeur chargement dans à l'initialisation de financeurComponent si 'l'on vient d'un client sinon

  // chargement à la selection d'un client dans financeurComponent.
  // forkjoin qui recupere toutes les infos liés au financeur
  /**
   * chargement à la selection d'un client dans financeurComponent.
   *
   * forkjoin qui recupere toutes les infos liés au financeur
   * @param {string} idFinanceur id du financeur
   */
  getInfoFinanceur(idFinanceur: string): Observable<boolean> {

    return forkJoin([this.getFinanceurById(idFinanceur), this.getAdresseFacturation(idFinanceur), this.getAdresseFinanceur(idFinanceur),
    this.getIban(idFinanceur), this.getCompte(idFinanceur), this.getEntite(idFinanceur), this.getContactFacturation(idFinanceur), this.getCompteEntiteFinanceur(idFinanceur)]).pipe(
      map((res: any) => {
        this.modeFacturationModel.formulaire.compte = this.modeFacturationModel.formulaire.compteCli;
        return true;
      })
    );
  }

  /**
   * recuperation des informations du financeur en mode 3
   * @param {string} idFinanceur id du financeur
   */
  getInfoInfinanaceurMode3(idFinanceur: string): Observable<boolean> {

    return forkJoin([this.getFinanceurById(idFinanceur),
    this.getIban(idFinanceur), this.getCompte(idFinanceur), this.getEntite(idFinanceur), this.getCompteEntiteFinanceur(idFinanceur)]).pipe(
      map((res: any) => {

        return true;
      })
    );
  }

  /**
   * retourne et affecte le financeur ou le client
   * @param {string} idFinanceur id du financeur
   */
  getFinanceurById(idFinanceur: string): Observable<ModeFacturationModel> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/INFOFINANCEUR/${idFinanceur}`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.dataSave.financeur = res.dataSave.financeur;
        return res;
      })
    );
  }

  /**
   * retourne et affecte l'adresse de facturation
   * @param {string} idFinanceur id du financeur
   */
  getAdresseFacturation(idFinanceur: string): Observable<ModeFacturationModel> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/ADRESSEFACTURATION/${idFinanceur}`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.adresseFacturation = res.formulaire.adresseFacturation;
        this.modeFacturationModel.formulaire.adresseFinanceurFacturation = res.formulaire.adresseFinanceurFacturation;
        return res;
      })
    );
  }

  /**
  * retourne et affecte le contact de facturation
  * @param {string} idFinanceur id du financeur
  */
  getContactFacturation(idFinanceur: string): Observable<ModeFacturationModel> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/CONTACTFACTURATION/${idFinanceur}`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.dataSave.contactFacturation = res.dataSave.contactFacturation;
        this.modeFacturationModel.formulaire.listContactFacturation = res.formulaire.listContactFacturation;
        return res;
      })
    );
  }

  /**
   * retourne et affecte l'adresse du financeur
   * @param {string} idFinanceur id du financeur
   */
  getAdresseFinanceur(idFinanceur: string): Observable<ModeFacturationModel> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/ADRESSEFINANCEUR/${idFinanceur}`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.adresseFinanceur = res.formulaire.adresseFinanceur;
        return res;
      })
    );
  }

  /**
   * Retourne la liste des comptes comptables par entité du financeur
   * @param idFinanceur
   */
  getCompteEntiteFinanceur(idFinanceur: string): Observable<QueovalItemModel> {
    return this.http.getHttp<QueovalItemModel>(`${this.indParam.AppParams.apiUrl}api/queovalitem/getData/CLIENT_ENTITE/${idFinanceur}`).pipe(
      map((result: QueovalItemModel) => {
        this.modeFacturationModel.formulaire.compteSurEntite = result.CompteSurEntite;
        return JSON.parse(JSON.stringify(this.modeFacturationModel));
      })
    );
  }

  /**
   * retourne et affecte les coordonnées banquaires du financeur
   * @param {string} idFinanceur id du financeur
   */
  getIban(idFinanceur: string): Observable<ModeFacturationModel> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/GETIBAN/${idFinanceur}`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.coordBancaire = res.formulaire.coordBancaire;
        let date: Date;
        if (this.modeFacturationModel.formulaire.coordBancaire.nouvelleDateSignature) {
          date = transformQueovalDateToDate(this.modeFacturationModel.formulaire.coordBancaire.nouvelleDateSignature);
          if (date.getFullYear() === 1) {
            this.modeFacturationModel.formulaire.coordBancaire.nouvelleDateSignature = null;
          } else {
            this.modeFacturationModel.formulaire.coordBancaire.nouvelleDateSignature = date;
          }
        } else {
          this.modeFacturationModel.formulaire.coordBancaire.nouvelleDateSignature = null;
        }

        if (this.modeFacturationModel.formulaire.coordBancaire.dateSignature) {
          date = transformQueovalDateToDate(this.modeFacturationModel.formulaire.coordBancaire.dateSignature);
          if (date.getFullYear() === 1) {
            this.modeFacturationModel.formulaire.coordBancaire.dateSignature = null;
          } else {
            this.modeFacturationModel.formulaire.coordBancaire.dateSignature = date;
          }
        } else {
          this.modeFacturationModel.formulaire.coordBancaire.dateSignature = null;
        }
        return res;
      })
    );
  }

  /**
   * retourne et affecte le compte du financeur
   * @param {string} idFinanceur id du financeur
   */
  getCompte(idFinanceur: string): Observable<ModeFacturationModel> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/COMPTECLIENT/${idFinanceur}`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.compteCli = res.formulaire.compte;
        return res;
      })
    );
  }

  /**
   * retourne et affecte la liste des entités
   * @param {string} idFinanceur id du financeur
   */
  getEntite(idFinanceur?: string, addEmptyEntite: boolean = true): Observable<boolean> {
    if (idFinanceur) {
      return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/LISTEENTITE/${idFinanceur}`).pipe(
        map((res: ModeFacturationModel) => {
          this.modeFacturationModel.formulaire.entite = res.formulaire.entite;
          //transformToSelect(res.formulaire, this.modeFacturationModel.formulaire, 'entite');
          if (addEmptyEntite) {
            AddEmptyLine(this.modeFacturationModel.formulaire.entite);
            if (this.modeFacturationModel.formulaire.entite.length === 2) {
              this.modeFacturationModel.formulaire.entite.splice(0, 1);
              this.modeFacturationModel.formulaire.entite[0].dateDerniereValidation = transformQueovalDateToDate(this.modeFacturationModel.formulaire.entite[0].dateDerniereValidation);
            } else {
              for (let i: number = 1; i < this.modeFacturationModel.formulaire.entite.length; i++) {
                this.modeFacturationModel.formulaire.entite[i].dateDerniereValidation = transformQueovalDateToDate(this.modeFacturationModel.formulaire.entite[i].dateDerniereValidation);
              }
            }
          } else {
            for (let i: number = 0; i < this.modeFacturationModel.formulaire.entite.length; i++) {
              this.modeFacturationModel.formulaire.entite[i].dateDerniereValidation = transformQueovalDateToDate(this.modeFacturationModel.formulaire.entite[i].dateDerniereValidation);
            }
          }
          return true;
        })
      );
    } else {
      return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/LISTEENTITE/0`).pipe(
        map((res: ModeFacturationModel) => {
          this.modeFacturationModel.formulaire.entite = res.formulaire.entite;
          //transformToSelect(res.formulaire, this.modeFacturationModel.formulaire, 'entite');
          if (addEmptyEntite) {
            AddEmptyLine(this.modeFacturationModel.formulaire.entite);
            if (this.modeFacturationModel.formulaire.entite.length === 2) {
              this.modeFacturationModel.formulaire.entite.splice(0, 1);
              this.modeFacturationModel.formulaire.entite[0].dateDerniereValidation = transformQueovalDateToDate(this.modeFacturationModel.formulaire.entite[0].dateDerniereValidation);
            } else {
              for (let i: number = 1; i < this.modeFacturationModel.formulaire.entite.length; i++) {
                this.modeFacturationModel.formulaire.entite[i].dateDerniereValidation = transformQueovalDateToDate(this.modeFacturationModel.formulaire.entite[i].dateDerniereValidation);
              }
            }
          } else {
            for (let i: number = 0; i < this.modeFacturationModel.formulaire.entite.length; i++) {
              this.modeFacturationModel.formulaire.entite[i].dateDerniereValidation = transformQueovalDateToDate(this.modeFacturationModel.formulaire.entite[i].dateDerniereValidation);
            }
          }
          return true;
        })
      );
    }
  }

  /**
  * retourne la liste des entités à partir de l'ident de l'utilisateur
  * @param {string} idFinanceur id du financeur
  */
  getEntiteFromUser(idUser?: string): Observable<boolean> {
    if (idUser) {
      return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/LISTEENTITEFROMUSER/${idUser}`).pipe(
        map((res: ModeFacturationModel) => {
          this.modeFacturationModel.formulaire.entite = res.formulaire.entite;
          // La valeur null a déjà été gérée coté serveur
          AddEmptyLine(this.modeFacturationModel.formulaire.entite, false);
          //transformToSelect(res.formulaire, this.modeFacturationModel.formulaire, 'entite', false);
          for (let i: number = 0; i < this.modeFacturationModel.formulaire.entite.length; i++) {
            if (this.modeFacturationModel.formulaire.entite[i].dateDerniereValidation) {
              this.modeFacturationModel.formulaire.entite[i].dateDerniereValidation = transformQueovalDateToDate(this.modeFacturationModel.formulaire.entite[i].dateDerniereValidation);
            }

          }
          return true;
        })
      );
    } else {
      // Récupération de toutes les entités
      return this.getEntite('0');
    }
  }


  /**
  * retourne et affecte le client donneur d'ordre
  * @param {string} idMission id de la Mission ou de l'affaire
  */
  getClientFromMission(idMission: string): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/CLIENTFROMMISSION/${idMission}`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.client = res.formulaire.client;
        return true;
      })
    );
  }

  /**
   * retourne et affecte la liste des objets facturables (conventions, missions, devis)
   * @param {string} idFinanceur id du financeur
   */
  getObjectsFacturabels(idFinanceur: string): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/OBJEFTFACTURABLE/${idFinanceur}`).pipe(
      map((res: ModeFacturationModel) => {
        //////for (const key of ['convention', 'devis', 'mission', 'prestation', 'stage']) {
        //////  transformToSelect(res.formulaire, this.modeFacturationModel.formulaire, key);
        //////}

        this.modeFacturationModel.formulaire.convention = res.formulaire.convention;
        this.modeFacturationModel.formulaire.devis = res.formulaire.devis;
        this.modeFacturationModel.formulaire.mission = res.formulaire.mission;
        this.modeFacturationModel.formulaire.prestation = res.formulaire.prestation;
        this.modeFacturationModel.formulaire.stage = res.formulaire.stage;

        AddEmptyLine(this.modeFacturationModel.formulaire.convention);
        AddEmptyLine(this.modeFacturationModel.formulaire.devis);
        AddEmptyLine(this.modeFacturationModel.formulaire.mission);
        AddEmptyLine(this.modeFacturationModel.formulaire.prestation);
        AddEmptyLine(this.modeFacturationModel.formulaire.stage);

        // les affaires sont affectées aux missions
        for (const a of res.formulaire.affaire) {
          const selectMission: ObjetFact = new ObjetFact();
          selectMission.id = a.id;
          selectMission.value = a.value;
          selectMission.client = a.client;
          selectMission.financeur = a.financeur;
          selectMission.devis = a.devis;
          selectMission.adresse = a.adresse;
          this.modeFacturationModel.formulaire.mission.push(selectMission);
        }

        if (this.modeFacturationModel.dataSave.prestation.id !== '0') {
          const prestation: Array<ObjetFact> = JSON.parse(JSON.stringify(this.modeFacturationModel.formulaire.mission));
          for (const a of res.formulaire.prestation) {
            const selectMission: ObjetFact = new ObjetFact();
            selectMission.id = a.id;
            selectMission.value = a.value;
            selectMission.client = a.client;
            selectMission.financeur = a.financeur;
            selectMission.devis = a.devis;
            this.modeFacturationModel.formulaire.mission.push(selectMission);
          }
          this.modeFacturationModel.formulaire.prestation = prestation;
        }
        return true;
      })
    );
  }

  /**
 * retourne et affecte la liste des objets facturables (conventions, missions, devis)
 * @param {string} idFinanceur id du financeur
 */
  getObjectsFacturables(idFinanceur: string): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/OBJEFTFACTURABLE/${idFinanceur}`).pipe(
      map((res: ModeFacturationModel) => {
        //for (const key of ['convention', 'devis', 'mission', 'prestation', 'stage']) {
        //  transformToSelect(res.formulaire, this.modeFacturationModel.formulaire, key);
        //}
        this.modeFacturationModel.formulaire.convention = res.formulaire.convention;
        this.modeFacturationModel.formulaire.devis = res.formulaire.devis;
        this.modeFacturationModel.formulaire.mission = res.formulaire.mission;
        this.modeFacturationModel.formulaire.prestation = res.formulaire.prestation;
        this.modeFacturationModel.formulaire.stage = res.formulaire.stage;

        AddEmptyLine(this.modeFacturationModel.formulaire.convention);
        AddEmptyLine(this.modeFacturationModel.formulaire.devis);
        AddEmptyLine(this.modeFacturationModel.formulaire.mission);
        AddEmptyLine(this.modeFacturationModel.formulaire.prestation);
        AddEmptyLine(this.modeFacturationModel.formulaire.stage);

        // les affaires sont affectées aux missions
        for (const a of res.formulaire.affaire) {
          const selectAffaire: ObjetFact = new ObjetFact(a.id, a.value);
          this.modeFacturationModel.formulaire.mission.push(selectAffaire);
        }
        return true;
      })
    );
  }

  // retourne et affecte la désignation
  /**
   * retourne et affecte la désignation
   * @param {string} idFinanceur id du financeur
   */
  getdesignation(idFinanceur: string): Observable<QueovalItemModel> {
    return this.http.getHttp<QueovalItemModel>(`${this.indParam.AppParams.apiUrl}api/queovalitem/getData/SEARCHBYID/${idFinanceur}|CLIENT`).pipe(
      map((res: QueovalItemModel) => {
        this.modeFacturationModel.formulaire.adresseFinanceur.designation =
          this.modeFacturationModel.formulaire.adresseFacturation.designation =
          res.queovalItemList[0].Text;
        return res;
      })
    );
  }
  // #endregion

  // #region mode Auto

  /**
   * forkjoin permettant de récupérer les listes, les informations des objets facturables et le mode de facturation -- OLD VERSION

   * @param {Array<BreadDik>} p liste des parametres du breadcrumb
   * @param {string} idMillesime id du millesime
   */
  //loadInfoAuto(p: Array<BreadDik>, idMillesime: string): Observable<boolean> {
  //  const identsBo: Array<string> = p[0].value.split('|');
  //  const idBo: string = identsBo[0].trim();
  //  const idFils: string = identsBo[1].trim();
  //  const idPere: string = identsBo[2].trim();
  //  const idRel: string = identsBo[3].trim();

  //return forkJoin([this.getListes(), this.getInfoObjetFacturable(`${idFils}|${idPere}|${idMillesime}`)]).pipe(
  //  mergeMap((res: Array<Boolean>) => {
  //    return forkJoin([this.getInfoFinanceur(this.modeFacturationModel.dataSave.financeur.id), this.getArticles(this.modeFacturationModel), this.getModeFacturation(`${idFils}|${idPere}|${idMillesime}`), this.getObjectsFacturabels(idPere)]).pipe(
  //      map((resu: Array<Boolean>) => {
  //        return true;
  //      })
  //    );
  //    })
  //  );
  //}

  /**
   * forkjoin permettant de récupérer les listes, les informations des objets facturables et le mode de facturation --- NEW VERSION (en récupérant chaque ident)

   * @param {string} idBo id du BO
   * @param {string} idFils id du Fils
   * @param {string} idPere id du Pere
   * @param {string} idRel id de la relation
   * @param {string} idMillesime id du millesime
   */
  loadInfoAuto2(idBo: string, idFils: string, idPere: string, idRel: string, idMillesime: string): Observable<boolean> {

    return forkJoin([this.getListes(), this.getInfoObjetFacturable(`${idFils}|${idPere}|${idMillesime}|0`)]).pipe(
      mergeMap((res: Array<Boolean>) => {
        return forkJoin([this.getInfoFinanceur(this.modeFacturationModel.dataSave.financeur.id), this.getArticles(this.modeFacturationModel), this.getModeFacturation(`${idFils}|${idPere}|${idMillesime}`), this.getObjectsFacturabels(idPere)]).pipe(
          map((resu: Array<Boolean>) => {
            return true;
          })
        );
      })
    );
  }

  /**
   * retourne et affecte les informations des objets facturables
   * @param {string} idFilsIdPereIdMillesime idFils|idPere(client)|idMillesime|idFinanceur
   */
  getInfoObjetFacturable(idFilsIdPereIdMillesime: string): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/INFOOBJETFACTURABLE/${idFilsIdPereIdMillesime}`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.dataSave.montantHtMission = res.dataSave.montantHtMission;
        this.modeFacturationModel.dataSave.montantHtEcheance = res.dataSave.montantHtEcheance;
        this.modeFacturationModel.dataSave.modeFacturation.montantHT = res.dataSave.montantHtMission;
        this.modeFacturationModel.dataSave.mission = res.dataSave.mission;
        this.modeFacturationModel.dataSave.prestation = res.dataSave.prestation;
        this.modeFacturationModel.dataSave.affaire = res.dataSave.affaire;
        this.modeFacturationModel.dataSave.convention = res.dataSave.convention;
        this.modeFacturationModel.dataSave.missionPrestation = res.dataSave.missionPrestation;
        this.modeFacturationModel.dataSave.toolTipObjetFacturable = res.dataSave.toolTipObjetFacturable;
        this.modeFacturationModel.dataSave.dateDebutMission = transformQueovalDateToDate(res.dataSave.dateDebutMission);
        this.modeFacturationModel.dataSave.millesimeMission = res.dataSave.millesimeMission;
        this.modeFacturationModel.dataSave.txFrais = res.dataSave.txFrais;
        this.modeFacturationModel.dataSave.financeur = res.dataSave.financeur;
        this.modeFacturationModel.dataSave.client = res.dataSave.client;
        this.modeFacturationModel.dataSave.codeAnalytique = res.dataSave.codeAnalytique;
        return true;
      })
    );
  }

  // retourne et affecte les modes de facturation
  /**
   * retourne et affecte les modes de facturation
   * @param {string} idFilsIdPereIdMillesime idFils|idPere|idMillesime
  */
  getModeFacturation(idFilsIdPereIdMillesime: string): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/MODEFACTURATION/${idFilsIdPereIdMillesime}`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.dataSave.modeFacturation.periodicite = res.dataSave.modeFacturation.periodicite;
        let isPeriodicite: boolean = false;
        if (this.modeFacturationModel.formulaire.typePeriodicite && this.modeFacturationModel.formulaire.typePeriodicite.length > 0) {
          for (const p of this.modeFacturationModel.formulaire.typePeriodicite) {
            if (p.id === this.modeFacturationModel.dataSave.modeFacturation.periodicite.id) {
              isPeriodicite = true;
              break;
            }
          }
          if (!isPeriodicite) {
            this.modeFacturationModel.dataSave.modeFacturation.periodicite = this.modeFacturationModel.formulaire.typePeriodicite[0];
          }
          this.modeFacturationModel.dataSave.modeFacturation.nbreOccurence = res.dataSave.modeFacturation.nbreOccurence;
          this.modeFacturationModel.dataSave.modeFacturation.periodiciteReglement = res.dataSave.modeFacturation.periodiciteReglement;
          let isPeriodiciteReglement: boolean = false;
          for (const p of this.modeFacturationModel.formulaire.typePeriodicite) {
            if (p.id === this.modeFacturationModel.dataSave.modeFacturation.periodiciteReglement.id) {
              isPeriodiciteReglement = true;
              break;
            }
          }
          if (!isPeriodiciteReglement) {
            this.modeFacturationModel.dataSave.modeFacturation.periodiciteReglement = this.modeFacturationModel.formulaire.typePeriodicite[0];
          }
        }
        this.modeFacturationModel.dataSave.modeFacturation.nbreOccurenceReglement = res.dataSave.modeFacturation.nbreOccurenceReglement;
        this.modeFacturationModel.dataSave.modeFacturation.groupeFamille = res.dataSave.modeFacturation.groupeFamille;
        this.modeFacturationModel.dataSave.modeFacturation.id = res.dataSave.modeFacturation.id;
        this.modeFacturationModel.dataSave.modeFacturation.type = res.dataSave.modeFacturation.type;
        this.modeFacturationModel.dataSave.modeFacturation.modeReglement = res.dataSave.modeFacturation.modeReglement;
        this.modeFacturationModel.dataSave.modeFacturation.article = res.dataSave.modeFacturation.article;
        this.modeFacturationModel.dataSave.modeFacturation.facturationFindemois = res.dataSave.modeFacturation.facturationFindemois === true;
        this.modeFacturationModel.dataSave.modeFacturation.reglementMoisPlus1 = res.dataSave.modeFacturation.reglementMoisPlus1 === true;
        this.modeFacturationModel.dataSave.modeFacturation.origine = res.dataSave.modeFacturation.origine;
        this.modeFacturationModel.dataSave.modeFacturation.jourReglement = res.dataSave.modeFacturation.jourReglement;
        this.modeFacturationModel.dataSave.modeFacturation.ordreLigne = res.dataSave.modeFacturation.ordreLigne;
        this.modeFacturationModel.dataSave.modeFacturation.groupeLigne = res.dataSave.modeFacturation.groupeLigne;

        if ((res.dataSave.modeFacturation.entiteFacturation.id === '' || res.dataSave.modeFacturation.entiteFacturation.id === '0') &&
          this.modeFacturationModel.formulaire.entite.length === 1) {
          this.modeFacturationModel.dataSave.modeFacturation.entiteFacturation = this.modeFacturationModel.formulaire.entite[0];
          for (const entite of this.modeFacturationModel.formulaire.entite) {
            if (this.modeFacturationModel.dataSave.modeFacturation.entiteFacturation.id === entite.id) {
              this.modeFacturationModel.formulaire.compte = entite.numeroCompte;
              break;
            }
          }
        } else if ((res.dataSave.modeFacturation.entiteFacturation.id === '' || res.dataSave.modeFacturation.entiteFacturation.id === '0') &&
          this.modeFacturationModel.formulaire.entite.length !== 1) {
          this.modeFacturationModel.dataSave.modeFacturation.entiteFacturation = new Select('0', '');
          //Affectation du numero de compte de l'entite
        } else {
          this.modeFacturationModel.dataSave.modeFacturation.entiteFacturation = res.dataSave.modeFacturation.entiteFacturation;
          for (const entite of this.modeFacturationModel.formulaire.entite) {
            if (this.modeFacturationModel.dataSave.modeFacturation.entiteFacturation.id === entite.id) {
              this.modeFacturationModel.formulaire.compte = entite.numeroCompte;
              break;
            }
          }
        }

        if (res.dataSave.modeFacturation.groupeFacturation.id === '') {
          this.modeFacturationModel.dataSave.modeFacturation.groupeFacturation = new Select('0', '');
        } else {
          this.modeFacturationModel.dataSave.modeFacturation.groupeFacturation = res.dataSave.modeFacturation.groupeFacturation;
        }

        if (res.dataSave.modeFacturation.dateDebutFacturation) {
          this.modeFacturationModel.dataSave.modeFacturation.dateDebutFacturation = res.dataSave.modeFacturation.dateDebutFacturation;
          if (this.modeFacturationModel.dataSave.modeFacturation.dateDebutFacturation.annee === '1') {
            if (this.modeFacturationModel.dataSave.modeFacturation.entiteFacturation.id !== '0') {
              this.modeFacturationModel.dataSave.modeFacturation.dateDebutFacturation = transformDateToQueovalDate(this.modeFacturationModel.formulaire.entite.filter((e: Entite) => e.id === this.modeFacturationModel.dataSave.modeFacturation.entiteFacturation.id)[0].dateDerniereValidation);
            } else {
              this.modeFacturationModel.dataSave.modeFacturation.dateDebutFacturation = new QueovalDate('0', '0', '0'); //null;
            }
          }
        }

        return true;
      })
    );
  }

  /**
   * retourne la liste des lignes automatiques
   * @param {ModeFacturationModel} model model
   */
  getLigneAuto(pModel: ModeFacturationModel): Observable<boolean> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'LISTELIGNESAUTO';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.groupeFacture = new Array<GroupeFactureModel>();
        this.modeFacturationModel.groupeFacture.push(new GroupeFactureModel);
        this.modeFacturationModel.groupeFacture[0].lignesFactures = res.lignes;
        this.modeFacturationModel.groupeFacture[0].lignesFactures.forEach((l: LigneFacture) => {
          l.dateFacturation = transformQueovalDateToDate(l.dateFacturation);
        });
        return true;
      }
      ));
  }

  /**
   * update d'une ligne de facturation en mode auto
   * @param {ModeFacturationModel} model model
   */
  updateTableAuto(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'INSERTLIGNEAUTO';
    model.lignes.forEach((l: LigneFacture) => {
      l.dateFacturation = transformDateToQueovalDate(l.dateFacturation);
    });
    //model.dataSave.modeFacturation.dateReglement = transformDateToQueovalDate(model.dataSave.modeFacturation.dateReglement);
    //model.dataSave.modeFacturation.dateDebutFacturation = transformDateToQueovalDate(model.dataSave.modeFacturation.dateDebutFacturation);
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        //this.modeFacturationModel.dataSave.acompte = res.dataSave.acompte;
        return res;
      })
    );
  }

  // #endregion

  // #region update

  /**
   * update les informations de compte du financeur
   * @param {ModeFacturationModel} model model
   */
  setIban(pModel: ModeFacturationModel): Observable<boolean> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'UPDATEIBAN';
    model.formulaire.coordBancaire.nouvelleDateSignature = transformDateToQueovalDate(model.formulaire.coordBancaire.nouvelleDateSignature);
    model.formulaire.coordBancaire.dateSignature = transformDateToQueovalDate(model.formulaire.coordBancaire.dateSignature);
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.coordBancaire = res.formulaire.coordBancaire;
        //////let date: Date;
        if (this.modeFacturationModel.formulaire.coordBancaire.nouvelleDateSignature) {
          this.modeFacturationModel.formulaire.coordBancaire.nouvelleDateSignature = transformQueovalDateToDateOrNull(this.modeFacturationModel.formulaire.coordBancaire.nouvelleDateSignature);
          //////////  if (date.getFullYear() === 1) {
          //////////    this.modeFacturationModel.formulaire.coordBancaire.nouvelleDateSignature = null;
          //////////  } else {
          //////////    this.modeFacturationModel.formulaire.coordBancaire.nouvelleDateSignature = date;
          //////////  }
          //////////} else {
          //////////  this.modeFacturationModel.formulaire.coordBancaire.nouvelleDateSignature = null;
        }

        if (this.modeFacturationModel.formulaire.coordBancaire.dateSignature) {
          this.modeFacturationModel.formulaire.coordBancaire.dateSignature = transformQueovalDateToDateOrNull(this.modeFacturationModel.formulaire.coordBancaire.dateSignature);
          //////  if (date.getFullYear() === 1) {
          //////    this.modeFacturationModel.formulaire.coordBancaire.dateSignature = null;
          //////  } else {
          //////    this.modeFacturationModel.formulaire.coordBancaire.dateSignature = date;
          //////  }
          //////} else {
          //////  this.modeFacturationModel.formulaire.coordBancaire.dateSignature = null;
        }
        return true;
      })
    );
  }

  /**
   * update les informations de l'adresse du financeur
   * @param {ModeFacturationModel} model model
   */
  saveAdresse(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'UPDATEADRESSE';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model);
  }

  // update les informations de temps passés
  /**
   * update les informations de temps passés
   * @param {ModeFacturationModel} model model
   */
  saveTempsPasse(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'UPDATETEMPS';
    model.lignes = model.lignes;
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model);

  }

  /**
   * update les informations du mode de facturation
   * @param {ModeFacturationModel} model model
   */
  updateModeFacturation(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'UPDATEMODEFACTURATION';
    //model.dataSave.modeFacturation.dateDebutFacturation = transformDateToQueovalDate(model.dataSave.modeFacturation.dateDebutFacturation);
    //model.dataSave.modeFacturation.dateReglement = transformDateToQueovalDate(model.dataSave.modeFacturation.dateReglement);
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model);
  }

  /**
   * update le commentaire d'une facture
   * @param {ModeFacturationModel} model model
   */
  updateCommentaireFacture(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'UPDATECOMMENTAIRE';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model);
  }

  /**
   * Génère les échéanciers à partir de plusieurs lignes de factures (ou missions)
   * @param model
   */
  genererEcheancierFacture(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'GENERERECHEANCIERFACTURE';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model);
  }

  // #endregion

  // #region préparation des factures

  /**
   * Charge les factures et tout ce dont le composant ModeFacturationComponent a besoin
   * @param {string} idFacture id de facture
   */
  loadFacture(idFacture: string): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/LOADFACTURE/${idFacture}`).pipe(
      mergeMap((res: ModeFacturationModel) => {
        if (res.HasBlockingAlert) {
          return of(false);
        } else {
          this.modeFacturationModel.dataSave.acompte = res.dataSave.acompte;
          this.modeFacturationModel.dataSave.client = res.dataSave.client;
          this.modeFacturationModel.dataSave.financeur = res.dataSave.financeur;
          this.modeFacturationModel.dataSave.idFacture = res.dataSave.idFacture;
          this.modeFacturationModel.dataSave.contactFacturation = res.dataSave.contactFacturation;
          this.modeFacturationModel.formulaire.listContactFacturation = res.formulaire.listContactFacturation;
          this.modeFacturationModel.formulaire.adresseFacturation = res.formulaire.adresseFacturation;
          this.modeFacturationModel.formulaire.compte = res.formulaire.compte;
          this.modeFacturationModel.dataSave.commentaire = res.dataSave.commentaire;
          this.modeFacturationModel.dataSave.modeFacturation.entiteFacturation = res.dataSave.modeFacturation.entiteFacturation;
          this.modeFacturationModel.dataSave.modeFacturation.groupeFacturation = res.dataSave.modeFacturation.groupeFacturation;
          this.modeFacturationModel.dataSave.modeFacturation.modeReglement = res.dataSave.modeFacturation.modeReglement;
          this.modeFacturationModel.dataSave.modeFacturation.periodiciteReglement = res.dataSave.modeFacturation.periodiciteReglement;
          this.modeFacturationModel.dataSave.modeFacturation.nbreOccurenceReglement = res.dataSave.modeFacturation.nbreOccurenceReglement;
          this.modeFacturationModel.dataSave.modeFacturation.dateDebutFacturation = res.dataSave.modeFacturation.dateDebutFacturation;
          this.modeFacturationModel.dataSave.modeFacturation.facturationFindemois = res.dataSave.modeFacturation.facturationFindemois;
          this.modeFacturationModel.dataSave.modeFacturation.dateReglement = res.dataSave.modeFacturation.dateReglement;
          this.modeFacturationModel.dataSave.modeFacturation.jourReglement = res.dataSave.modeFacturation.jourReglement;
          this.modeFacturationModel.dataSave.modeFacturation.reglementMoisPlus1 = res.dataSave.modeFacturation.reglementMoisPlus1;
          this.modeFacturationModel.dataSave.modeFacturation.origine = res.dataSave.modeFacturation.origine;
          this.modeFacturationModel.dataSave.modeFacturation.ordreLigne = res.dataSave.modeFacturation.ordreLigne;
          this.modeFacturationModel.dataSave.modeFacturation.groupeLigne = res.dataSave.modeFacturation.groupeLigne;
          this.modeFacturationModel.formulaire.adresseFinanceur = res.formulaire.adresseFinanceur;
          // this.modeFacturationModel.formulaire.txTva = res.formulaire.txTva;

          this.modeFacturationModel.dataSave.echeancier = res.dataSave.echeancier;
          this.modeFacturationModel.dataSave.echeancier.forEach((e: Echeancier) => {
            e.date = transformQueovalDateToDate(e.date);
          });
          this.modeFacturationModel.groupeFacture = res.groupeFacture;
          for (const g of this.modeFacturationModel.groupeFacture) {
            for (const l of g.lignesFactures) {
              l.dateFacturation = transformQueovalDateToDate(l.dateFacturation);
            }
          }
          const getInfoFinanceur: Observable<boolean> = this.getInfoInfinanaceurMode3(res.dataSave.financeur.id);
          const getEtatSepa: Observable<boolean> = this.getEtatSepa();
          return forkJoin([getInfoFinanceur, getEtatSepa, this.getListes(idFacture)]).pipe(
            mergeMap((resu: Array<boolean>) => {
              const listArticle: Observable<boolean> = this.getArticles(this.modeFacturationModel);
              const listGroupe: Observable<boolean> = this.getGroupeFacturation(res.dataSave.modeFacturation.entiteFacturation.id);
              const objectFacturable: Observable<boolean> = this.getObjectsFacturabels(res.dataSave.client.id);
              return forkJoin([listArticle, listGroupe, objectFacturable]).pipe(
                map((result: Array<boolean>) => {
                  if (this.modeFacturationModel.dataSave.modeFacturation.groupeFacturation.id === '0' || this.modeFacturationModel.dataSave.modeFacturation.groupeFacturation.id === '') {
                    this.modeFacturationModel.dataSave.modeFacturation.groupeFacturation.value = 'Aucun groupe de facturation sélectionné';
                  } else {
                    for (const g of this.modeFacturationModel.formulaire.groupeFacturation) {
                      if (g.id === this.modeFacturationModel.dataSave.modeFacturation.groupeFacturation.id) {
                        this.modeFacturationModel.dataSave.modeFacturation.groupeFacturation.value = g.value;
                        break;
                      }
                    }
                  }

                  if (this.modeFacturationModel.dataSave.modeFacturation.entiteFacturation.id === '0' || this.modeFacturationModel.dataSave.modeFacturation.entiteFacturation.id === '') {
                    this.modeFacturationModel.dataSave.modeFacturation.entiteFacturation.value = 'Aucune entité sélectionnée';
                  } else {
                    for (const e of this.modeFacturationModel.formulaire.entite) {
                      if (e.id === this.modeFacturationModel.dataSave.modeFacturation.entiteFacturation.id) {
                        this.modeFacturationModel.dataSave.modeFacturation.entiteFacturation.value = e.value;
                        break;
                      }
                    }
                  }

                  if (this.modeFacturationModel.dataSave.modeFacturation.modeReglement.id === '0' || this.modeFacturationModel.dataSave.modeFacturation.modeReglement.id === '') {
                    this.modeFacturationModel.dataSave.modeFacturation.modeReglement.value = 'Aucun mode de règlement sélectionné';
                  } else {

                    for (const r of this.modeFacturationModel.formulaire.modeReglement) {
                      if (r.id === this.modeFacturationModel.dataSave.modeFacturation.modeReglement.id) {
                        this.modeFacturationModel.dataSave.modeFacturation.modeReglement.value = r.value;
                        break;
                      }
                    }
                  }
                  return true;
                })
              );
            })
          );
        }
      })
    );
  }

  // getPreparation(value: string): Observable<ModeFacturationModel> {
  //   return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/PREPARATIONAUTO/${value}`);
  // }

  /**
   * retourne les periodes dans la préparation de facturation
   * @param {string} idEntite id de l'entite
   */
  getPeriode(idEntite: string): Observable<ModeFacturationModel> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/PERIODE/${idEntite}`);
  }

  // getTypeFacture(): Observable<ModeFacturationModel> {
  //   return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/TYPEFACTURE/0`);
  // }

  /**
   * supression des factures en mode préparation des factures
   * @param {ModeFacturationModel} model model
   */
  deleteFacture(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'DELETEFACTURE';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model);
  }

  decalerJourFacturation(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'UPDATEDATEFACTURES';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model);
  }

  /**
   * préparation des factures dans l'étapes de préparation des factures
   * @param {ModeFacturationModel} model model
   */
  preparationAuto(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'PREPARATIONAUTOASYNC';
    // model.preparation.periodeFacturation = transformDateToQueovalDate(model.preparation.periodeFacturation);
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model);
  }

  generationLignesEnCours(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'ISGENERATIONENCOURS';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model);
  }

  /**
   * prépare la préparation des factures
   * @param {ModeFacturationModel} model model
   */
  preparationEnCours(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'ISPREPARATIONENCOURS';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model);
  }

  validationAuto(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'VALIDATIONASYNC';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model);
  }

  validationEnCours(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'ISVALIDATIONENCOURS';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model);
  }

  // update les reglements
  updateReglement(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'UPDATEREGLEMENT';
    //model.dataSave.modeFacturation.dateDebutFacturation = transformDateToQueovalDate(model.dataSave.modeFacturation.dateDebutFacturation);
    //model.dataSave.modeFacturation.dateReglement = transformDateToQueovalDate(model.dataSave.modeFacturation.dateReglement);
    model.dataSave.echeancier.forEach((e: Echeancier) => {
      e.date = transformDateToQueovalDate(e.date);
    });
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model);
  }

  // recupere les temps par lignes (forfait/temps passé)
  getTempsPasseByLine(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'LIGNETEMPS';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model);
  }

  // #endregion

  // #region array

  // création des lignes du tableau en mode automatique
  createLigneAuto(pModel: ModeFacturationModel): Observable<boolean> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'CREATELIGNEAUTO';
    model.dataSave.calculEcheance.dateDebut = transformDateToQueovalDate(model.dataSave.calculEcheance.dateDebut);
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        if (this.modeFacturationModel.groupeFacture.length > 0) {
          res.lignes.forEach((l: LigneFacture) => {
            l.dateFacturation = transformQueovalDateToDate(l.dateFacturation);
            this.modeFacturationModel.groupeFacture[0].lignesFactures.push(l);
          });
        } else {
          this.modeFacturationModel.groupeFacture = new Array<GroupeFactureModel>();
          const groupe: GroupeFactureModel = new GroupeFactureModel();
          this.modeFacturationModel.groupeFacture.push(groupe);
          this.modeFacturationModel.groupeFacture[0].lignesFactures = res.lignes;
          this.modeFacturationModel.groupeFacture[0].lignesFactures.forEach((l: LigneFacture) => {
            l.dateFacturation = transformQueovalDateToDate(l.dateFacturation);
          });
        }
        return true;
      }));
  }

  // création des docX
  CreateDocX(idFacture: string): Observable<FileModel> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/GENERATIONDOCX/${idFacture}`).pipe(
      map((res: ModeFacturationModel) => {
        let dlModel: FileModel = new FileModel();
        return dlModel = {
          DownloadMode: 'DOCXGENERE',
          Extension: 'docx',
          Name: this.modeFacturationModel.dataSave.idFacture,
          FullPath: '',
          folderTypeDoc: '',
          folderBase: '',
          Ident: '',
          LstIdent: new Array<string>(),
          SaveFile: true,
          DisplayName: ''
          // FullName: this.commonPage.modelContext.ModelName + '.docx'
        };
      }));
  }

  // Merge des pdf des factures
  GenereMergedFacturePdf(pModel: ModeFacturationModel): Observable<FileModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'MERGE_FACTURE_PDF';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/`, model).pipe(
      map((res: ModeFacturationModel) => {
        let dlModel: FileModel = new FileModel();
        return dlModel = {
          DownloadMode: 'OPENPDF',
          Extension: 'pdf',
          Name: 'factures.pdf',
          FullPath: res.dataSave.titreFacture,
          folderTypeDoc: '',
          folderBase: '',
          Ident: '',
          LstIdent: new Array<string>(),
          SaveFile: false,
          DisplayName: ''
        };
      }));
  }

  // création des lignes du tableau en mode libre lorsqu'un devis est selectionné
  getLigneProForma(pModel: ModeFacturationModel): Observable<boolean> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'CREATELIGNEPROFORMA';
    //model.dataSave.modeFacturation.dateReglement = transformDateToQueovalDate(model.dataSave.modeFacturation.dateReglement);
    //model.dataSave.modeFacturation.dateDebutFacturation = transformDateToQueovalDate(model.dataSave.modeFacturation.dateDebutFacturation);
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      mergeMap((res: ModeFacturationModel) => {
        this.modeFacturationModel.dataSave.idFacture = res.dataSave.idFacture;
        this.modeFacturationModel.dataSave.acompte = res.dataSave.acompte;
        this.modeFacturationModel.dataSave.commentaire = res.dataSave.commentaire;
        return this.getGroupeLigne(res.dataSave.idFacture).pipe(
          map((result: boolean) => {
            this.modeFacturationModel.groupeFacture = res.groupeFacture;
            this.modeFacturationModel.groupeFacture.forEach((g: GroupeFactureModel) => {
              g.lignesFactures.forEach((l: LigneFacture) => {
                l.dateFacturation = transformQueovalDateToDate(l.dateFacturation);
                //VerifyAndAffectIfValue(this.modeFacturationModel.formulaire, l, 'groupeLigne');
                //VerifyAndAffectIfValue(this.modeFacturationModel.formulaire, l, 'txTva');
                //VerifyAndAffectIfValue(this.modeFacturationModel.formulaire, l, 'article');
                //VerifyAndAffectIfValue(this.modeFacturationModel.formulaire, l, 'typeRemise');
                //VerifyAndAffectIfValue(this.modeFacturationModel.formulaire, l, 'unite');
                //VerifyAndAffectIfValue(this.modeFacturationModel.formulaire, l, 'modeReglement');
                VerifyAndAffectIfValue(this.modeFacturationModel.formulaire.groupeLigne, l.groupeLigne);
                VerifyAndAffectIfValue(this.modeFacturationModel.formulaire.txTva, l.txTva);
                VerifyAndAffectIfValue(this.modeFacturationModel.formulaire.article, l.article);
                VerifyAndAffectIfValue(this.modeFacturationModel.formulaire.typeRemise, l.typeRemise);
                VerifyAndAffectIfValue(this.modeFacturationModel.formulaire.unite, l.unite);
                VerifyAndAffectIfValue(this.modeFacturationModel.formulaire.modeReglement, l.modeReglement);
              });
            });
            return result;
          })
        );
      }));
  }

  // validation des lignes de factures
  getValidation(idFacture: string, idEntite: string): Observable<boolean> {
    let isError: boolean = false;
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/VALIDATION/${idFacture}|${idEntite}`).pipe(
      map((res: ModeFacturationModel) => {
        for (const alert of res.CodedAlerts) {
          if (alert.Code === 'error') {
            isError = true;
            break;
          }
        }
        if (isError) {
          return false;
        } else {
          this.modeFacturationModel.dataSave.idFacture = '';
          this.modeFacturationModel.lignes = new Array<LigneFacture>();
          this.modeFacturationModel.groupeFacture = new Array<GroupeFactureModel>();
          return true;
        }
      })
    );
  }

  // suppression d'une ligen en mode libre
  delete(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'DELETELIGNE';
    model.lignes.forEach((l: LigneFacture) => {
      l.dateFacturation = transformDateToQueovalDate(l.dateFacturation);
    });
    model.dataSave.echeancier.forEach((e: Echeancier) => {
      e.date = transformDateToQueovalDate(e.date);
    });
    //model.dataSave.modeFacturation.dateReglement = transformDateToQueovalDate(model.dataSave.modeFacturation.dateReglement);
    //model.dataSave.modeFacturation.dateDebutFacturation = transformDateToQueovalDate(model.dataSave.modeFacturation.dateDebutFacturation);
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.dataSave.idFacture = res.dataSave.idFacture;
        this.modeFacturationModel.dataSave.acompte = res.dataSave.acompte;
        return res;
      })
    );
  }

  // suppression d'une ligne en mode automatique
  deleteAuto(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'DELETELIGNEAUTO';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        return res;
      })
    );
  }

  // delier une ligne en mode validation
  delier(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'DELIERLIGNEAUTO';
    //model.dataSave.modeFacturation.dateReglement = transformDateToQueovalDate(model.dataSave.modeFacturation.dateReglement);
    //model.dataSave.modeFacturation.dateDebutFacturation = transformDateToQueovalDate(model.dataSave.modeFacturation.dateDebutFacturation);
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model);
  }

  // modification d'une ligne en mode libre
  updateTable(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel));
    model.Mode = 'CREATELIGNE';
    model.lignes.forEach((l: LigneFacture) => {
      l.dateFacturation = transformDateToQueovalDate(l.dateFacturation);
    });
    model.dataSave.echeancier.forEach((e: Echeancier) => {
      e.date = transformDateToQueovalDate(e.date);
    });
    //model.dataSave.modeFacturation.dateReglement = transformDateToQueovalDate(model.dataSave.modeFacturation.dateReglement);
    //model.dataSave.modeFacturation.dateDebutFacturation = transformDateToQueovalDate(model.dataSave.modeFacturation.dateDebutFacturation);
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.dataSave.idFacture = res.dataSave.idFacture;
        this.modeFacturationModel.dataSave.echeancier = res.dataSave.echeancier;
        this.modeFacturationModel.dataSave.acompte = res.dataSave.acompte;
        this.modeFacturationModel.dataSave.commentaire = res.dataSave.commentaire;
        if (res.groupeFacture) {
          res.groupeFacture.forEach((g: GroupeFactureModel) => {
            g.lignesFactures.forEach((l: LigneFacture) => {
              l.dateFacturation = transformQueovalDateToDate(l.dateFacturation);
            });
          });
        }
        this.modeFacturationModel.dataSave.echeancier.forEach((e: Echeancier) => {
          e.date = transformQueovalDateToDate(e.date);
        });
        return res;
      })
    );
  }

  // update des groupes de lignes
  updateGroupe(pModel: ModeFacturationModel): Observable<ModeFacturationModel> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel))
    model.Mode = 'UPDATEGROUPE';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model);
  }

  // update d'une facture utilisé uniquement mode 3 à la validation de l'étape2
  updateFacture(pModel: ModeFacturationModel): Observable<boolean> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel))
    model.Mode = 'UPDATEFACTURE';
    //model.dataSave.modeFacturation.dateDebutFacturation = transformDateToQueovalDate(model.dataSave.modeFacturation.dateDebutFacturation);
    //model.dataSave.modeFacturation.dateReglement = transformDateToQueovalDate(model.dataSave.modeFacturation.dateReglement);
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.dataSave.echeancier = res.dataSave.echeancier;
        this.modeFacturationModel.dataSave.echeancier.forEach((e: Echeancier) => {
          e.date = transformQueovalDateToDate(e.date);
        });
        return true;
      })
    );
  }
  // #endregion

  // #region bo-facturation
  getInfoBoFacturation(idFacture: string): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/IDFINANCEURBOFACTURATION/${idFacture}`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.infoBo = res.infoBo;
        return true;
      })
    );
  }

  getSolde(idFacture: string): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/SOLDE/${idFacture}`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.dataSave.montantTTCSolde = res.dataSave.montantTTCSolde;
        this.modeFacturationModel.dataSave.montantTTCSoldeARegle = res.dataSave.montantTTCSoldeARegle;
        this.modeFacturationModel.dataSave.montantTTCSoldeRegle = res.dataSave.montantTTCSoldeRegle;
        return true;
      })
    );
  }

  getTitreFacture(idFacture: string): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/TITREFACTURE/${idFacture}`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.dataSave.titreFacture = res.dataSave.titreFacture;
        return true;
      })
    );
  }

  soldeFacture(pModel: ModeFacturationModel): Observable<boolean> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel))
    model.Mode = 'SOLDE';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        return true;
      })
    );
  }

  genereDocXpdf(idFacture: string): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/GENERATIONDOCXPDF/${idFacture}`).pipe(
      map((res: ModeFacturationModel) => {
        return true;
      })
    );
  }

  contrepassation(idFacture: string): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/CONTREPASSATION/${idFacture}`).pipe(
      map((res: ModeFacturationModel) => {
        if (res.dataSave.idFacture === '') {
          return false;
        } else {
          this.modeFacturationModel.dataSave.idFacture = res.dataSave.idFacture;
          return true;
        }
      })
    );
  }
  // #endregion

  // #region recherche

  // retourne et affecte la liste des états disponible pour les facture (pour la recherche)
  getEtatFacture(): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/ETATFACTURE/0`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.formulaire.etatFacture = res.formulaire.etatFacture;
        //transformToSelect(res.formulaire, this.modeFacturationModel.formulaire, 'etatFacture');
        AddEmptyLine(this.modeFacturationModel.formulaire.etatFacture);
        return true;
      })
    );
  }

  // retourne la liste des types responsables et affecte dans les select
  getTypeResponsables(): Observable<boolean> {
    return this.http.getHttp<ReconduiteModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/reconduite/LISTTYPERESPONSABLE/0`).pipe(
      map((res: ReconduiteModel) => {
        this.reconduiteModel.ListTypeResponsable = res.ListTypeResponsable;
        //transformToSelect(res, this.reconduiteModel, 'ListTypeResponsable', true);
        AddEmptyLine(this.reconduiteModel.ListTypeResponsable);
        return true;
      })
    );
  }

  // retourne la liste des types responsables et affecte dans les select
  getCollaborateurs(): Observable<boolean> {
    return this.http.getHttp<ReconduiteModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/reconduite/LISTCOLLABORATEUR/0`).pipe(
      map((res: ReconduiteModel) => {
        this.reconduiteModel.ListCollaborateur = res.ListCollaborateur;
        //transformToSelect(res, this.reconduiteModel, 'ListCollaborateur', true);
        AddEmptyLine(this.reconduiteModel.ListCollaborateur);
        return true;
      })
    );
  }

  // #endregion

  updateClotureAndValid(pModel: ModeFacturationModel): Observable<boolean> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel))
    model.Mode = 'UPDATECLOTUREANDVALID';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        return true;
      })
    );
  }

  getModeFacturationPrestation(): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/MODEREMONTERPRESTATIONFACTURATION/0`).pipe(
      map((res: ModeFacturationModel) => {
        this.modeFacturationModel.prestation.modeRemonterPrestationFact = res.prestation.modeRemonterPrestationFact;
        return true;
      })
    );
  }

  getListFichierSepa(): Observable<boolean> {
    return this.http.getHttp<ReglementModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/reglements/GET_LISTE_FICHIER_SEPA/0`).pipe(
      map((res: ReglementModel) => {
        this.reglementModel.ListFichiersSEPA = res.ListFichiersSEPA;
        this.reglementModel.ListFichiersSEPA.forEach((l: FichierSEPA) => {
          l.Date = transformQueovalDateToDate(l.Date);
        });
        return true;
      })
    );
  }

  updateListFichierSepa(pModel: ReglementModel): Observable<boolean> {
    const model: ReglementModel = JSON.parse(JSON.stringify(pModel))
    model.Mode = 'UPDATE_LISTE_FICHIER_SEPA';
    return this.http.postHttp<ReglementModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/reglements`, model).pipe(
      map((res: ReglementModel) => {
        this.reglementModel.ListFichiersSEPA = res.ListFichiersSEPA;
        this.reglementModel.ListFichiersSEPA.forEach((l: FichierSEPA) => {
          l.Date = transformQueovalDateToDate(l.Date);
        });
        return true;
      })
    );
  }

  genererFichierSEPA(pModel: ReglementModel): Observable<boolean> {
    const model: ReglementModel = JSON.parse(JSON.stringify(pModel))
    model.Mode = 'GENERATE_FICHIER_SEPA';
    for (let i: number = 0; i < model.ListPrelevementsSEPA.length; i++) {
      model.ListPrelevementsSEPA[i].Date_REG = transformDateToQueovalDate(model.ListPrelevementsSEPA[i].Date_REG);
    }
    return this.http.postHttp<ReglementModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/reglements`, model).pipe(
      map((res: ReglementModel) => {
        this.reglementModel.ListFichiersSEPA = res.ListFichiersSEPA;
        this.reglementModel.ListFichiersSEPA.forEach((l: FichierSEPA) => {
          l.Date = transformQueovalDateToDate(l.Date);
        });
        return true;
      })
    );
  }

  getNumeroFichierSEPA(pModel: ReglementModel): Observable<boolean> {
    const model: ReglementModel = JSON.parse(JSON.stringify(pModel))
    model.Mode = 'GET_LISTE_NUMERO_FICHIER_SEPA';
    return this.http.postHttp<ReglementModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/reglements`, model).pipe(
      map((res: ReglementModel) => {
        this.reglementModel.ListNumeroPrelevementSepa = res.ListNumeroPrelevementSepa;
        return true;
      })
    );
  }

  UpdateContactFacturation(pModel: ModeFacturationModel): Observable<boolean> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel))
    model.Mode = 'UPDATECONTACTFACTURATION';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        return true;
      })
    );
  }

  UpdateFinanceurMission(pModel: ModeFacturationModel): Observable<boolean> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel))
    model.Mode = 'CREATE_LIEN_FINANCEUR_MISSION';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        return true;
      })
    );
  }

  updateMissionPrestation(pModel: ModeFacturationModel): Observable<boolean> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel))
    model.Mode = 'CREATE_LIEN_MISSION_PRESTATION';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        return true;
      })
    );
  }

  updateOrder(pModel: ModeFacturationModel): Observable<boolean> {
    const model: ModeFacturationModel = JSON.parse(JSON.stringify(pModel))
    model.Mode = 'UPDATEORDER';
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        return true;
      })
    );
  }

  updateAutValid(idFacture: string, strValueToUpdate: string): Observable<boolean> {
    // Création du model
    const model: ModeFacturationModel = new ModeFacturationModel();
    model.Mode = 'UPDATEAUTVALID';
    model.dataSave = new DataSave();
    model.dataSave.idFacture = idFacture;
    // Modification de l'authorisation à mettre à jour (création de la future valeur inverse)
    model.dataSave.isAuthorizedToValid = !(strValueToUpdate === 'true');
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        return true;
      })
    );
  }


  /**
  * retourne et affecte la liste des objets facturables (conventions, missions, prestation)
  * @param {string} idPersonneMorale id de la personne morale
  */
  getListeMissionPrestationConvention(idPersonneMorale: string): Observable<boolean> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/OBJEFTFACTURABLE/${idPersonneMorale}`).pipe(
      map((res: ModeFacturationModel) => {

        this.modeFacturationModel.formulaire.convention = res.formulaire.convention;
        this.modeFacturationModel.formulaire.mission = res.formulaire.mission;
        this.modeFacturationModel.formulaire.prestation = res.formulaire.prestation;

        AddEmptyLine(this.modeFacturationModel.formulaire.convention);
        AddEmptyLine(this.modeFacturationModel.formulaire.mission);
        AddEmptyLine(this.modeFacturationModel.formulaire.prestation);

        // les affaires sont affectées aux missions
        for (const a of res.formulaire.affaire) {
          const selectMission: ObjetFact = new ObjetFact();
          selectMission.id = a.id;
          selectMission.value = a.value;
          selectMission.client = a.client;
          selectMission.financeur = a.financeur;
          selectMission.devis = a.devis;
          this.modeFacturationModel.formulaire.mission.push(selectMission);
        }


        return true;
      })
    );
  }


  createLienLigneFactureMission(idLigneFacture: string, idMission: string, idPrestation: string, idConvention: string): Observable<boolean> {
    // Création du model
    const model: ModeFacturationModel = new ModeFacturationModel();
    model.Mode = 'CREATE_LIEN_LIGNEFACTURE_MISSION';
    model.dataSave = new DataSave();
    model.dataSave.mission.id = idMission;
    model.dataSave.prestation.id = idPrestation;
    model.dataSave.convention.id = idConvention;
    const ligne: LigneFacture = new LigneFacture();
    ligne.id = idLigneFacture;
    model.lignes.push(ligne);
    return this.http.postHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation`, model).pipe(
      map((res: ModeFacturationModel) => {
        return true;
      })
    );
  }

  getListesInfoProduitTache(idTache: string): Observable<ModeFacturationModel> {
    return this.http.getHttp<ModeFacturationModel>(`${this.indParam.AppParams.apiUrl}api/spefCompta/modeFacturation/PRODUITTACHEAFFAIRE/${idTache}`).pipe(
      map((res: ModeFacturationModel) => {
        // Mode de facturation de l'affaire
        this.modeFacturationModel.dataSave.modeFacturation = res.dataSave.modeFacturation;

        // Information de l'affaire rattache à la tache
        this.modeFacturationModel.formulaire.affaire = res.formulaire.affaire;

        // Liste des groupes de lignes
        this.modeFacturationModel.formulaire.groupeLigne = res.formulaire.groupeLigne;
        // Liste des groupes de famille
        this.modeFacturationModel.formulaire.groupeFamille = res.formulaire.groupeFamille;
        const selectUnique: Select = new Select(this.modeFacturationModel.formulaire.affaire[0].id, `Facturation isolée`);
        this.modeFacturationModel.formulaire.groupeFamille.unshift(selectUnique);
        AddEmptyLine(this.modeFacturationModel.formulaire.groupeFamille);
        // Liste taux de tva
        this.modeFacturationModel.formulaire.txTva = res.formulaire.txTva;
        // Liste des articles
        this.modeFacturationModel.formulaire.article = res.formulaire.article;
        // Liste des unités
        this.modeFacturationModel.formulaire.unite = res.formulaire.unite;

        // Devis de l'affaire
        this.modeFacturationModel.dataSave.devis = res.dataSave.devis;
        // Millesime de l'affaire
        this.modeFacturationModel.dataSave.millesime = res.dataSave.millesime;
        // Affaire selectionnée
        this.modeFacturationModel.dataSave.mission = res.dataSave.mission;
        // Client sur l'affaire
        this.modeFacturationModel.dataSave.client = res.dataSave.client;
        // Financeur sur l'affaire
        this.modeFacturationModel.dataSave.financeur = res.dataSave.financeur;
        // Informations specifiques Affaire Tache Produit
        this.modeFacturationModel.formulaire.produitTacheAffaire = res.formulaire.produitTacheAffaire;
        // Adresse de facturation 
        this.modeFacturationModel.formulaire.adresseFacturation = res.formulaire.adresseFacturation;
        // Adresse du financeur
        this.modeFacturationModel.formulaire.adresseFinanceur = res.formulaire.adresseFinanceur;

        return this.modeFacturationModel;
      })
    );
  }



}

